import { ErrorCodeEnum, OcrResult, Vendor, VendorTypeEnum } from "@finway-group/shared/lib/models"
import { ExpensePaymentOptionEnum } from "@finway-group/shared/lib/models/expense/expensePaymentOption.enum"
import { ReadFileAiRequest, ReadFileAiResponse } from "@finway-group/shared/lib/models/file/file.api"
import * as Sentry from "@sentry/react"
import { LabeledValue } from "antd/lib/select"
import axios, { isCancel } from "axios"
import HttpStatus from "http-status"
import { ConfirmOCRChangesModal } from "~Components/modals/confirmOCRChanges.modal"
import { i18n } from "~Shared/locales/i18n"
import { ExpenseService, NotificationService, VendorService } from "~Shared/services"
import { isHospitalityExpense } from "~Shared/utils/expense.utils"
import { isEmpty } from "~Shared/utils/helper.utils"
import {
    compareFormOcrData,
    formatOCRObject,
    formatOcrObjectForAutoGeneratedCardRequests,
    mergeExistingAndOcrItems,
    updateOcrItems,
    updatingAttendees,
} from "~Shared/utils/ocr.utils"
import { BuildOcrUpdateDataParams, RunOcrAiParams, RunOcrParams, SplitDataType, UpdatedOcrObject } from "~Shared/utils/ocr.utils.types"

const errorTitleByCode: { [key: string]: string } = {
    // OCR error
    [ErrorCodeEnum.OCR_NO_DOCUMENT_PROVIDED]: "error:ocr.title",
    [ErrorCodeEnum.OCR_NO_DATA_IN_DOCUMENT]: "error:ocr.title",
    [ErrorCodeEnum.OCR_READ_ERROR]: "error:ocr.title",
    [ErrorCodeEnum.OCR_READ_ERROR_INVALID_BASE_64]: "error:ocr.title",
    [ErrorCodeEnum.OCR_READ_ERROR_INVALID_URL]: "error:ocr.title",
    [ErrorCodeEnum.OCR_READ_ERROR_DOCUMENT_COULD_NOT_BE_LOADED]: "error:ocr.title",
    [ErrorCodeEnum.OCR_READ_ERROR_TIMEOUT]: "error:ocr.title",
    [ErrorCodeEnum.OCR_READ_ERROR_UNEXPECTED_RESPONSE]: "error:ocr.title",
    [ErrorCodeEnum.OCR_READ_ERROR_EMPTY_DOCUMENT]: "error:ocr.title",
    [ErrorCodeEnum.OCR_READ_ERROR_INVALID_FILE_ID]: "error:ocr.title",
    [ErrorCodeEnum.OCR_CONTENT_TYPE_UNDETECTABLE]: "error:ocr.title",
    [ErrorCodeEnum.OCR_CONTENT_TYPE_INVALID]: "error:ocr.title",
    [ErrorCodeEnum.OCR_PAGE_RANGE_INVALID]: "error:ocr.title",
    // Error
    [ErrorCodeEnum.OCR_INVALID_DOCUMENT_DATA]: "error:error",
    [ErrorCodeEnum.OCR_INVALID_EINVOICE_TYPE]: "error:error",
    [ErrorCodeEnum.OCR_EINVOICE_DOES_NOT_PASS_VALIDATION]: "error:error",
    [ErrorCodeEnum.OCR_UNIVERSAL_INVOICE_DOES_NOT_PASS_VALIDATION]: "error:error",
    // E-Invoice error
    [ErrorCodeEnum.OCR_ONLY_ONE_DOCUMENT_ALLOWED]: "error:einvoice.title",
    [ErrorCodeEnum.OCR_UNSUPPORTED_EINVOICE_FORMAT]: "error:einvoice.title",
    [ErrorCodeEnum.OCR_INVALID_EINVOICE]: "error:einvoice.title",
    [ErrorCodeEnum.OCR_PREFERRED_PROVIDER_NOT_FOUND]: "error:einvoice.title",
}

export type VendorReadFileAiResponse = ReadFileAiResponse & {
    vendorId?: string
}

export const OcrService = {
    uploadAndRetrieveAi: async ({ fileId, req, abortSignal }: { fileId: string; req: ReadFileAiRequest; abortSignal: AbortSignal }) => {
        const { data } = await axios.post<ReadFileAiResponse>(`files/${fileId}/read-file-ai`, req, { signal: abortSignal })
        return data
    },
    runOcrAi: async (params: RunOcrAiParams): Promise<VendorReadFileAiResponse | undefined> => {
        try {
            params.setIsOcrUploading(true)
            const aiOcrRes = await OcrService.uploadAndRetrieveAi({
                fileId: params.fileId,
                req: { documentType: params.documentType },
                abortSignal: params.abortSignal,
            })
            const supplier = aiOcrRes?.ocrData?.supplier
            const vendorArgs: Partial<Pick<Vendor, "name" | "iban">> = { name: supplier?.name }
            if (supplier && "ibanNumber" in supplier && supplier.ibanNumber) {
                vendorArgs.iban = supplier.ibanNumber
            }
            if (!isEmpty(vendorArgs)) {
                const foundVendor = await OcrService.determineVendor(vendorArgs)
                return { ...aiOcrRes, vendorId: foundVendor?.id }
            }
            return aiOcrRes
        } catch (aiOcrError: unknown) {
            if (isCancel(aiOcrError)) {
                return undefined
            }
            NotificationService.showErrorNotificationBasedOnResponseError(aiOcrError, i18n.t("error:ocr.title"))
            return undefined
        } finally {
            params.setIsOcrUploading(false)
        }
    },
    determineVendor: async (vendorData: Partial<Pick<Vendor, "name" | "iban">>) => {
        try {
            const vendor = await VendorService.findBestVendorMatch(vendorData)
            return vendor
        } catch (error) {
            Sentry.captureException(`[OCR Service] error: ${typeof error === "string" ? error : JSON.stringify(error)}`)
            return undefined
        }
    },
    uploadAndRetrieve: async (requestData: { filePath: string; isHospitality: boolean; ocrItemizationEnabled: boolean; isOcrRetrigger: boolean; meta?: object }) => {
        const { data } = await axios.post<OcrResult>("files/read-invoice", requestData)
        return data
    },
    /** @returns `false` if the user cancelled adding the document after being warned about number mismatches, `true` otherwise. */
    runOcr: async ({
        invoiceUrl,
        expense,
        setIsOcrUploading,
        t,
        noDataDifferenceFunction,
        setUpdatedObject,
        showModal,
        language,
        isOcrItemizationEnabled,
        meta,
        isShowingVendorChangeIfNotFound,
        showOcrDifferencesModal = true,
        isOcrRetrigger = false,
        handleInvoice,
    }: RunOcrParams) => {
        try {
            setIsOcrUploading(true)

            const isHospitality = isHospitalityExpense(expense)
            const ocrItemizationEnabled = isOcrItemizationEnabled || isHospitality

            const ocrObject = await OcrService.uploadAndRetrieve({ filePath: invoiceUrl, isHospitality, ocrItemizationEnabled, isOcrRetrigger, meta })

            const ocrObjectWithFormattedTaxRates = OcrService.formatOcrObjectTaxRate(ocrObject)

            if (!isOcrRetrigger && ocrObjectWithFormattedTaxRates.invoiceNumber) {
                const hasDuplicate = await ExpenseService.checkHasDuplicate(expense, ocrObjectWithFormattedTaxRates.invoiceNumber)
                if (hasDuplicate) {
                    delete ocrObjectWithFormattedTaxRates.invoiceNumber
                }
            }

            const foundVendor = await OcrService.determineVendor({ name: ocrObject.vendor?.name, iban: ocrObject.vendor?.iban })

            try {
                await handleInvoice(ocrObject)
            } catch (err) {
                Sentry.captureException(`[OCR Service (handleInvoice)] Error: ${typeof err === "string" ? err : JSON.stringify(err)}`)
            }

            const formOCRDataDifferences = compareFormOcrData({ expense, ocrObject: ocrObjectWithFormattedTaxRates, foundVendor, t, isShowingVendorChangeIfNotFound }, language)

            const shouldNotShowDifferencesModal = !formOCRDataDifferences || !showOcrDifferencesModal

            if (shouldNotShowDifferencesModal) {
                const ocrObjectWithUpdatedSplits = ocrObjectWithFormattedTaxRates.splits
                    ? { ...ocrObjectWithFormattedTaxRates, splits: mergeExistingAndOcrItems(expense.splits, ocrObjectWithFormattedTaxRates.splits) }
                    : { ...ocrObjectWithFormattedTaxRates }
                return await noDataDifferenceFunction(ocrObjectWithUpdatedSplits)
            }

            showModal(ConfirmOCRChangesModal, true, {
                isShowing: true,
                expense,
                ocrObject: ocrObjectWithFormattedTaxRates,
                formOCRDataDifferences,
                onClose: () => {},
                setUpdatedObject,
            })
            return true
        } catch (err) {
            let title = "error:ocr.title"
            if (axios.isAxiosError(err) && err.response?.status === HttpStatus.BAD_REQUEST) {
                const code = err.response?.data?.errorCode || ""
                if (code && errorTitleByCode[code]) {
                    title = errorTitleByCode[code]
                }
            }

            NotificationService.showErrorNotificationBasedOnResponseError(err, t(title))
            return true
        } finally {
            setIsOcrUploading(false)
        }
    },
    formatOcrObjectTaxRate: (ocrObject: OcrResult) => ({
        ...ocrObject,
        ...(ocrObject.taxRate && { taxRate: ExpenseService.getExpenseTaxRate(ocrObject.taxRate) }),
        ...(ocrObject.splits && {
            splits: ocrObject.splits.map((split) => ({
                ...split,
                ...(split.taxRate && { taxRate: ExpenseService.getExpenseTaxRate(split.taxRate) }),
            })),
        }),
    }),
    buildOcrUpdateData: async ({ ocrObject, existingData, ocrItemizationEnabled, isHospitality, requester }: BuildOcrUpdateDataParams): Promise<UpdatedOcrObject> => {
        const ocrObjectWithFormattedTaxes = OcrService.formatOcrObjectTaxRate(ocrObject)
        const { vendor } = ocrObject

        let foundVendor
        try {
            foundVendor = await VendorService.findBestVendorMatch({ name: vendor?.name, iban: vendor?.iban })
        } catch (error) {
            Sentry.captureException(`[OCR Service] error: ${typeof error === "string" ? error : JSON.stringify(error)}`)
        }

        const attendees: Array<LabeledValue> | undefined = isHospitality ? updatingAttendees(existingData, requester) : undefined

        const newOcrObject = {
            ...ocrObjectWithFormattedTaxes,
            ...(isHospitality && attendees && { attendees }),
            ...(foundVendor && { vendor: { ...ocrObject.vendor, _id: foundVendor.id }, vendorType: VendorTypeEnum.VENDOR }),
        }

        if (!foundVendor) {
            delete newOcrObject.vendor
        }

        const isAutoGeneratedCardRequest = existingData.isAutoGenerated && existingData.paymentOption === ExpensePaymentOptionEnum.SMART_CARD

        if (isAutoGeneratedCardRequest) {
            const updatedOcrItems =
                ocrObject.splits && ocrItemizationEnabled
                    ? updateOcrItems({
                          ocrItemizationEnabled,
                          ocrObject: newOcrObject,
                          expense: existingData,
                          isHospitality,
                      })
                    : {
                          splits: existingData.splits,
                      }

            const ocrObjectWithUpdatedSplits = { ...newOcrObject, ...(updatedOcrItems && { ...updatedOcrItems }) }

            return formatOcrObjectForAutoGeneratedCardRequests(existingData, ocrObjectWithUpdatedSplits)
        }

        const updatedOcrItems: SplitDataType =
            ocrObject.splits && ocrItemizationEnabled
                ? updateOcrItems({
                      ocrItemizationEnabled,
                      ocrObject: newOcrObject,
                      expense: existingData,
                      isHospitality,
                  })
                : {
                      splits: existingData.splits,
                  }

        const ocrObjectWithUpdatedSplits = { ...newOcrObject, ...(updatedOcrItems && { ...updatedOcrItems }) }

        const ocrObjectWithUpdatedTaxRate = {
            ...ocrObjectWithUpdatedSplits,
        }

        return formatOCRObject(ocrObjectWithUpdatedTaxRate)
    },
}
